// Paid ticketing (Stripe Connect) — organizer surfaces. Connect status +
// onboarding are org-scoped; ticket tiers + orders are per-event. Backed by the
// `payments` feature's custom actions (quickback/features/payments/actions/*).
import { api } from "./api"

// ── Connect (org-level) ─────────────────────────────────────────────────────

export type ConnectStatus = {
  connected: boolean
  accountId?: string
  chargesEnabled: boolean
  payoutsEnabled: boolean
  onboardingStatus: "none" | "pending" | "complete" | string
}

export function connectStatusKey() {
  return ["payments-connect-status"] as const
}

export function connectStatusQueryOptions() {
  return {
    queryKey: connectStatusKey(),
    queryFn: () => api<ConnectStatus>("/api/v1/payments/connect/status", { method: "POST", body: {} }),
  }
}

export function startConnectOnboarding() {
  return api<{ url: string; accountId: string }>("/api/v1/payments/connect/onboarding-link", {
    method: "POST",
    body: {},
  })
}

// ── Ticket types (per-event) ────────────────────────────────────────────────

export type TicketType = {
  id: string
  eventId: string
  organizationId: string
  name: string
  description: string | null
  priceCents: number
  currency: string
  quantityTotal: number | null
  quantityReserved: number
  quantitySold: number
  active: number
  sortOrder: number
}

type TicketsPayload = { success: true; tickets: TicketType[] }

export function ticketsKey(eventId: string) {
  return ["event-tickets", eventId] as const
}

export function ticketsQueryOptions(eventId: string) {
  return {
    queryKey: ticketsKey(eventId),
    queryFn: () =>
      api<TicketsPayload>("/api/v1/payments/tickets/list", { method: "POST", body: { eventId } }),
    select: (p: TicketsPayload) => p.tickets,
  }
}

export type TicketInput = {
  name: string
  description?: string | null
  priceCents: number
  currency?: string
  quantityTotal?: number | null
  active?: boolean
  sortOrder?: number
}

export function createTicket(eventId: string, input: TicketInput) {
  return api<{ success: true; ticket: TicketType }>("/api/v1/payments/tickets/create", {
    method: "POST",
    body: { eventId, ...input },
  })
}

export function updateTicket(ticketTypeId: string, patch: Partial<TicketInput>) {
  return api<{ success: true; ticket?: TicketType; unchanged?: true }>(
    "/api/v1/payments/tickets/update",
    { method: "POST", body: { ticketTypeId, ...patch } },
  )
}

export function deleteTicket(ticketTypeId: string) {
  return api<{ success: true; ticketTypeId: string }>("/api/v1/payments/tickets/delete", {
    method: "POST",
    body: { ticketTypeId },
  })
}

// ── Billing package status (per-event) ──────────────────────────────────────
// Mirrors the backend `assertTicketingActivated` gate so the organizer UI never
// offers a ticket action the server will reject. The event-status action is
// org-scoped (admin/owner), which is exactly who views the ticketing settings.

export type EventBillingStatus =
  | { configured: false }
  | {
      configured: true
      packages: { mgmt: string; ticketing: string; vault: string; cventSync: string; branding: string }
    }

export function eventBillingStatusKey(eventId: string) {
  return ["event-billing-status", eventId] as const
}

export function eventBillingStatusQueryOptions(eventId: string) {
  return {
    queryKey: eventBillingStatusKey(eventId),
    queryFn: () =>
      api<EventBillingStatus>("/api/v1/billing/event-status", { method: "POST", body: { eventId } }),
  }
}

// True when the ticketing surface should be available. Mirrors
// assertTicketingActivated: the ticketing package is paid/comped, OR billing is
// entirely unarmed (no platform plan) — the backend fails OPEN in that case so
// pre-billing flows keep working, and the UI must match or it would hide a
// button the server would happily accept.
export function ticketingActive(status: EventBillingStatus | undefined): boolean {
  if (!status) return false
  if (!status.configured) return true
  const s = status.packages.ticketing
  return s === "paid" || s === "comped"
}

// ── Orders (per-event) ──────────────────────────────────────────────────────

export type TicketOrder = {
  id: string
  ticketTypeId: string
  buyerEmail: string
  buyerName: string | null
  amountCents: number
  applicationFeeCents: number
  currency: string
  status: "pending" | "paid" | "refunded" | "failed" | "expired" | string
  paidAt: string | null
  createdAt?: string | null
}

type OrdersPayload = { success: true; orders: TicketOrder[] }

export function ordersKey(eventId: string) {
  return ["event-orders", eventId] as const
}

export function ordersQueryOptions(eventId: string) {
  return {
    queryKey: ordersKey(eventId),
    queryFn: () =>
      api<OrdersPayload>("/api/v1/payments/orders/list", { method: "POST", body: { eventId } }),
    select: (p: OrdersPayload) => p.orders,
  }
}

// ── Money helpers ───────────────────────────────────────────────────────────

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  CAD: "$",
  AUD: "$",
  GBP: "£",
  EUR: "€",
}

// Deterministic money formatter. We intentionally do NOT use Intl.NumberFormat:
// the SSR runtime (workerd ICU) and the browser's ICU can emit subtly different
// currency strings (symbol/spacing), which trips React hydration (#418) on the
// SSR'd ticketing page. Manual grouping keeps server + client byte-identical.
export function formatMoney(cents: number, currency = "usd"): string {
  const cur = currency.toUpperCase()
  const sym = CURRENCY_SYMBOL[cur]
  const neg = cents < 0
  const abs = Math.abs(cents)
  const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  const frac = (abs % 100).toString().padStart(2, "0")
  const body = sym ? `${sym}${whole}.${frac}` : `${cur} ${whole}.${frac}`
  return neg ? `-${body}` : body
}

/** Parse a user-typed dollar string ("49.99") to integer cents. */
export function dollarsToCents(value: string): number | null {
  const n = Number.parseFloat(value.replace(/[^0-9.]/g, ""))
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100)
}
